import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { Icon } from '../Icon/Icon';
import './NotificationViewer.scss';

/**
 * NotificationViewer Component
 *
 * A reusable component to display validation warnings and errors in a clean, left-aligned format.
 * Supports single or multiple warnings with bullet point formatting.
 *
 * @param {Array} warnings - Array of warning objects with message and severity
 * @param {string} className - Additional CSS classes
 * @param {string} multipleMessage - Custom message for multiple warnings
 *
 * @example
 * // Single warning
 * <NotificationViewer warnings={[{ message: 'Field is required', severity: 'error' }]} />
 *
 * @example
 * // Multiple warnings
 * <NotificationViewer
 *   warnings={[
 *     { message: 'Amount mismatch', severity: 'warning' },
 *     { message: 'Date conflict', severity: 'error' }
 *   ]}
 *   multipleMessage="Multiple issues found"
 * />
 */

// Helper function to separate errors and warnings
const separateWarnings = (warnings) => {
  const errors = warnings.filter((w) => w.severity === 'error');
  const nonErrors = warnings.filter((w) => w.severity !== 'error');
  return { errors, nonErrors };
};

// Helper function to generate stable key
const generateWarningKey = (warning, index) => {
  return `${warning.severity}-${warning.message.slice(0, 20)}-${index}`;
};

// Helper function to safely render HTML content using DOMPurify
const SafeHTMLRenderer = ({ content, prefix = '' }) => {
  const sanitizedContent = DOMPurify.sanitize(prefix + content, {
    ALLOWED_TAGS: ['strong', 'b', 'i', 'em', 'span'],
    ALLOWED_ATTR: ['class'],
  });

  return <span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
};

SafeHTMLRenderer.propTypes = {
  content: PropTypes.string.isRequired,
  prefix: PropTypes.string,
};

const NotificationViewer = React.memo(({ warnings = [], className = '' }) => {
  // Memoized render functions (must be before any early returns)
  const renderErrors = useCallback(
    (errors) => (
      <div className='notification-viewer__errors'>
        <div className='notification-viewer__errors-header'>
          <Icon
            name='Warning_Circle'
            size='sm'
            className='notification-viewer__errors-icon'
          />
        </div>
        <div className='notification-viewer__errors-list'>
          {errors.map((error, index) => (
            <div
              key={generateWarningKey(error, index)}
              className='notification-viewer__error-item'
            >
              <span>• </span>
              <SafeHTMLRenderer content={error.message} />
            </div>
          ))}
        </div>
      </div>
    ),
    []
  );

  const renderWarnings = useCallback(
    (warnings) => (
      <div className='notification-viewer__notification__warnings'>
        {warnings.map((warning, index) => (
          <div
            key={generateWarningKey(warning, index)}
            className='notification-viewer__notification__warning-item'
          >
            <Icon
              name='Warning'
              size='sm'
              className='notification-viewer__notification__warning-icon'
            />
            <p>{warning.message}</p>
          </div>
        ))}
      </div>
    ),
    []
  );

  const renderGroupedNotifications = useCallback(() => {
    const { errors, nonErrors } = separateWarnings(warnings);

    return (
      <div className='notification-viewer__containers'>
        {nonErrors.length > 0 && (
          <div className='notification-viewer__warning-container'>
            <div className='notification-viewer__notification notification-viewer__notification--warning'>
              <div className='notification-viewer__notification__content'>
                {renderWarnings(nonErrors)}
              </div>
            </div>
          </div>
        )}
        {errors.length > 0 && (
          <div className='notification-viewer__error-container'>
            <div className='notification-viewer__notification notification-viewer__notification--error'>
              <div className='notification-viewer__notification__content'>
                {renderErrors(errors)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [warnings, renderErrors, renderWarnings]);

  // Early return for empty warnings (after all hooks)
  if (!warnings || warnings.length === 0) {
    return null;
  }

  return (
    <div className={`notification-viewer ${className}`.trim()}>
      {renderGroupedNotifications()}
    </div>
  );
});

// Set display name for debugging
NotificationViewer.displayName = 'NotificationViewer';

NotificationViewer.propTypes = {
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      severity: PropTypes.oneOf(['error', 'warning', 'info']).isRequired,
    })
  ),
  className: PropTypes.string,
};

export { NotificationViewer };
